import { datadogLogs } from "@datadog/browser-logs";
import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { LtiLaunchToken } from "@examspark/ims-lti";
import { SsoLoginToken } from "@examspark/types";

import { useQueryGetLaunchToken } from "../api/operations/launch";
import AppLoading from "../components/AppLoading";
import { useTokenAuth } from "../hooks/useTokenAuth";

export type LTIContextData = {
  token: LtiLaunchToken;
};

export const isLtiLaunchToken = (
  token: LtiLaunchToken | SsoLoginToken | undefined,
): token is LtiLaunchToken => token?.purpose === "lti";

export const LTIContext = React.createContext<LTIContextData | undefined>(
  undefined,
);

/**
 * Where this application is embedded in an LTI context, this component provides
 * meangful information about that context to the application.
 *
 * @todo show session expired modal when the LTI session is no longer available
 * @todo handle page reloads better (development papercut when HMR forces a refresh)
 *
 * @returns
 */
const LTIProvider = (
  props: PropsWithChildren<{
    cognitoUserPoolId: string;
  }>,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  // exchange guid for token
  // similar logic in apps/cms/src/components/lti/Lti.tsx
  /**
   * @todo consider moving this into `useTokenAuth`, but it will require some
   * refactor because `token` can now either be a `jwt` or a `guid`.
   */
  const [search] = useSearchParams();
  const isTokenGuid = search.get("tokenType") === "guid";
  const [token, setToken] = useState(search.get("token"));
  const [tokenId] = useState(isTokenGuid ? search.get("token") : null);

  const { data: tokenIdRecord } = useQueryGetLaunchToken(tokenId);

  useEffect(() => {
    if (tokenIdRecord?.token) {
      setToken(tokenIdRecord.token ?? null);
    }
  }, [tokenIdRecord?.token]);

  const { loginState, decoded } = useTokenAuth(token, "lti");

  useEffect(() => {
    if (!isLtiLaunchToken(decoded)) return;
    // Strip the ?token from the URL so the user cannot navigate back to it.
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname, decoded]);

  if (loginState === "in_progress") {
    //
    // Don't render children before we finish setting signin state.
    // The inconsistency between signed-in user and state user will
    // cause bad behavior with ProtectedRoutes.
    //
    return <AppLoading />;
  }

  return (
    <LTIProvider.Inner token={decoded}>{props.children}</LTIProvider.Inner>
  );
};

const LTIProviderInner = (
  props: React.PropsWithChildren<{
    token: LtiLaunchToken | SsoLoginToken | undefined;
  }>,
) => {
  const ltiContext = useMemo<LTIContextData | undefined>(() => {
    if (!isLtiLaunchToken(props.token)) return undefined;
    return { token: props.token };
  }, [props.token]);

  useEffect(() => {
    datadogLogs.setGlobalContextProperty("lti", ltiContext);
  }, [ltiContext]);

  return (
    <LTIContext.Provider value={ltiContext}>
      {props.children}
    </LTIContext.Provider>
  );
};

LTIProvider.Inner = LTIProviderInner;

export default LTIProvider;
